import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ServicesScroll = () => {
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);
    const titleRef = useRef(null);

    const services = [
        { icon: 'fa-user-graduate', title: 'Career Counseling', desc: 'Expert guidance to choose the right career path based on your interests and goals' },
        { icon: 'fa-university', title: 'University Selection', desc: 'Get matched with the best universities that fit your profile and budget' },
        { icon: 'fa-file-alt', title: 'SOP & LOR Guidance', desc: 'Professional assistance for Statement of Purpose and Letter of Recommendation' },
        { icon: 'fa-money-check-alt', title: 'Education Loan Assistance', desc: 'Help with education loans and financial planning for your studies' },
        { icon: 'fa-passport', title: 'Visa Processing', desc: 'Complete visa guidance with high success rate and documentation support' },
        { icon: 'fa-plane-departure', title: 'Pre-Departure Support', desc: 'Comprehensive orientation and support before you fly to your destination' },
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation with kinetic typography effect
            gsap.fromTo(
                titleRef.current,
                {
                    opacity: 0,
                    y: 50,
                    scale: 0.9,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 50%',
                        scrub: 1,
                    },
                }
            );

            // Cards staggered animation with morphing effect
            cardsRef.current.forEach((card, index) => {
                gsap.fromTo(
                    card,
                    {
                        opacity: 0,
                        y: 80,
                        scale: 0.8,
                        rotateY: index % 2 === 0 ? -15 : 15,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        rotateY: 0,
                        duration: 1,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 85%',
                            end: 'top 50%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="services"
            className="py-20 bg-lightGray relative overflow-hidden"
        >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E63946' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header with kinetic typography */}
                <div ref={titleRef} className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-handshake text-primary-red text-2xl"></i>
                        <h2 className="section-title">Complete Study Abroad Solutions</h2>
                    </div>
                    <p className="section-subtitle">End-to-end support for your international education journey</p>
                </div>

                {/* Services Grid with morphing animations */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                    {services.map((service, index) => (
                        <div
                            key={index}
                            ref={(el) => (cardsRef.current[index] = el)}
                            className="service-box bg-white p-10 rounded-2xl text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl group"
                        >
                            <div className="service-icon group-hover:scale-110 transition-transform duration-500">
                                <i className={`fas ${service.icon}`}></i>
                            </div>
                            <h3 className="text-xl font-semibold text-black mb-4">{service.title}</h3>
                            <p className="text-gray">{service.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServicesScroll;
